// ONE DESCRIPTION PER PLAN — the dev plane's epic stories as data.
//
// Split from `plans-mocks.ts` when the consolidation below took that file past
// the 300-line mock ceiling (`mock-size.test.ts`). The seam is data vs
// derivation: this module says what each plan IS, and `plans-mocks.ts` turns one
// {@link PlanStory} into the two endpoints that serve it. The ceiling exists
// because this corpus is READ like a server — "which family answers this
// address, and what does it answer" — and that question should not cost a scroll
// through four plans' sample slices to reach the dispatcher.
//
// WHAT MUST NOT COME BACK. A story is the SINGLE description of its plan: the
// four `plans`-row facts, the timestamps, and the groups. Nothing here declares
// what the index row says about the WORK — no `groupCount`, no `mergedCount`,
// no `leadTitle`, no `repoFullName` — because `summarise` derives every one of
// those from `groups`. Re-declaring a summary beside a story is exactly the
// defect this consolidation deleted: CodeRabbit found one disagreement on PR
// #3780 and a sweep of all four plans found TEN across eight fields, because two
// hand-maintained descriptions of one plan sat in one file. If a future story
// needs a count the groups cannot supply, the answer is a group that produces
// it, never a number typed beside them.
// `plan_mock_multi` is the CROSS-REPO story (arc 101 slice 05): one plan,
// three repos — the alphabetical lead-vs-array trap its docblock spells out.
// TYPED AGAINST `PlanProjectionGroup` — the domain's own wire shape, which
// `apps/api` serves VERBATIM. `disposition` and `stallReason` are DERIVED by
// `planGroupDisposition`, the same function the server classifies with, so a
// slice's badge in a dev story cannot disagree with the one prod sends. The
// plan-level `source` rides the same rule one level up: NO story declares it —
// the dispatcher derives it off the id with `planSourceOf`, so a lane badge
// the server would not derive for an id is not expressible here.

import {
  type PlanGroupHold,
  type PlanInitiator,
  type PlanOwner,
  type PlanProjectionPullRequest,
  type PlanProjectionTask,
  type PlanSummary,
  planGroupDisposition,
} from '@developerz/domain';
import { at } from './mock-session';
import type { StoryGroup } from './plan-leg-mocks';

/**
 * The three repos these stories live in. Named rather than inlined because a
 * plan's repo is now a fact about each GROUP, and the summary's two repo fields
 * have to agree with the groups the detail fixture answers for the same id.
 */
const GATEWAY = 'developerz-ai/api-gateway';
const DOCS = 'developerz-ai/docs-site';
const WEB = 'developerz-ai/web-ui';

/**
 * One slice, disposition DERIVED rather than declared. `outcomeKind: 'pr'` is
 * explicit because the classifier requires it — omitting it re-creates #2949.
 */
function group(input: {
  groupId: string;
  position: number;
  title: string;
  /** THIS slice's repo — one repo, one box, one PR. Required, as the schema is. */
  repoFullName: string;
  status: StoryGroup['status'];
  dependsOn: readonly string[];
  task: PlanProjectionTask | null;
  pullRequest: PlanProjectionPullRequest | null;
  heldOn?: PlanGroupHold | null; // Why the task hasn't started (#4341); defaults null.
}): StoryGroup {
  const verdict = planGroupDisposition({
    groupStatus: input.status,
    task:
      input.task === null
        ? null
        : {
            status: input.task.status,
            pullRequestNumber: input.task.pullRequestNumber,
            outcomeKind: 'pr',
            noWorkSettled: false, // no mock slice hit #4235 or #5137
            opsActionRefused: false,
          },
    pullRequestState: input.pullRequest?.state ?? null,
  });
  const { heldOn = null, ...rest } = input;
  return { ...rest, dependsOn: [...input.dependsOn], ...verdict, noWork: null, heldOn };
}

/** A merged slice and the PR it landed — the shape all three take once done. */
function merged(
  id: string,
  position: number,
  title: string,
  number: number,
  repoFullName: string,
): StoryGroup {
  return group({
    groupId: id,
    position,
    title,
    repoFullName,
    status: 'merged',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_slice_${position + 1}`, status: 'merged', pullRequestNumber: number },
    // `mergeCommitSha: null` (#3702) — these stories exercise plan rendering, not
    // deploy proof, and null is what a row merged before that column existed carries.
    pullRequest: {
      number,
      state: 'merged',
      title,
      mergeable: null,
      mergeDecision: 'auto',
      mergeCommitSha: null,
    },
  });
}

const SLICE_TITLES = [
  'Extract the rate-limit middleware',
  'Emit budget headers on every /v1 response',
  'Document the back-off contract',
] as const;

/**
 * The cross-repo epic's three slices, ONE REPO EACH, in plan order. The lead
 * (position 0) lives in `web-ui`, which is LAST of the three alphabetically: a
 * reader that paints `repoFullNames` in wire order, or one name for the whole
 * plan, disagrees with these groups and with nothing else in the family.
 */
function multiRepoGroups(): StoryGroup[] {
  return [
    merged('grp_1', 0, 'Move the console onto the back-off contract', 4201, WEB),
    group({
      groupId: 'grp_2',
      position: 1,
      title: SLICE_TITLES[1],
      repoFullName: GATEWAY,
      status: 'dispatched',
      // Cross-repo ordering is spelled exactly like same-repo ordering — the
      // upstream is a sibling GROUP id, with no repo term anywhere in the edge.
      dependsOn: ['grp_1'],
      task: { id: 'tsk_slice_2', status: 'in_progress', pullRequestNumber: null },
      pullRequest: null,
    }),
    group({
      groupId: 'grp_3',
      position: 2,
      title: SLICE_TITLES[2],
      repoFullName: DOCS,
      status: 'pending',
      dependsOn: ['grp_2'],
      task: { id: 'tsk_slice_3', status: 'pending', pullRequestNumber: null },
      pullRequest: null,
    }),
  ];
}

/** Every slice merged — the terminal story, nothing left to stop. */
function doneGroups(): StoryGroup[] {
  return SLICE_TITLES.map((title, i) => merged(`grp_${i + 1}`, i, title, 4101 + i, GATEWAY));
}

/**
 * One merged, one in flight, one waiting — the default story. ITS LEAD IS ITS
 * OWN HEADLINE: the index row is derived from these groups, so a card that
 * disagreed with its page is not expressible.
 */
function liveGroups(): StoryGroup[] {
  return [
    merged('grp_1', 0, 'Give /v1 a back-off contract', 4101, GATEWAY),
    group({
      groupId: 'grp_2',
      position: 1,
      title: SLICE_TITLES[0],
      repoFullName: GATEWAY,
      status: 'dispatched',
      dependsOn: ['grp_1'],
      task: { id: 'tsk_slice_2', status: 'in_progress', pullRequestNumber: null },
      pullRequest: null,
    }),
    group({
      groupId: 'grp_3',
      position: 2,
      title: SLICE_TITLES[2],
      repoFullName: GATEWAY,
      status: 'pending',
      dependsOn: ['grp_2'],
      // `pending`, NOT `queued`: the latter is not a member of `TaskStatus`
      // and never was — the annotation is what says so.
      task: { id: 'tsk_slice_3', status: 'pending', pullRequestNumber: null },
      pullRequest: null,
    }),
  ];
}

/**
 * The DEAD-TAIL story: a slice whose run ended with the task failed, so nothing
 * can ever flip it to `merged`. It is what puts the destructive "1 stalled" chip
 * on the index — the count the merged fraction alone could never say — and the
 * index row now gets that 1 by tallying THESE groups rather than by assertion.
 */
function stalledGroups(): StoryGroup[] {
  return [
    group({
      groupId: 'grp_1',
      position: 0,
      title: SLICE_TITLES[2],
      repoFullName: DOCS,
      status: 'dispatched',
      dependsOn: [],
      task: { id: 'tsk_stalled_1', status: 'failed', pullRequestNumber: null },
      pullRequest: null,
    }),
    group({
      groupId: 'grp_2',
      position: 1,
      title: 'Link the back-off page from the API reference',
      repoFullName: DOCS,
      status: 'pending',
      dependsOn: ['grp_1'],
      task: null,
      pullRequest: null,
    }),
  ];
}

/**
 * ONE plan, described once. The four `plans`-row facts are declared (no group
 * can supply them); everything the index row says about the WORK is derived
 * from `groups` by {@link summarise}, and the mint lane is derived from the id
 * by `planSourceOf` — it is a fact about the ID, never a `plans`-row fact, so
 * it has no field here (the declared spelling it replaced served a badge the
 * server would not derive for the id, #4404's review).
 */
export type PlanStory = {
  readonly planId: string;
  /** The `plans` ROW's lifecycle — NOT a roll-up of the groups. */
  readonly status: PlanSummary['status'];
  /** The `raw_tasks` ask this plan was authored from, or null. */
  readonly rawTaskId: string | null;
  /** Who started it — the `plans` row's own stamp (#4340); see `PlanInitiator`. */
  readonly initiatedBy: PlanInitiator;
  readonly owner: PlanOwner | null; // resolved, or null — the common case (#4477)
  readonly createdIso: string;
  readonly updatedIso: string;
  readonly groups: StoryGroup[];
};

export const STORIES: readonly PlanStory[] = [
  {
    // The live epic: the fallback for any id naming no other case, so a row and
    // the page it opens agree. The ONE 'user' story, under the ONE `run_`-minted
    // id — the scout lane's only mint prefix (`planSourceOf`). #4404 fixed a
    // stale `scout` badge that used to live here under an id that derives `custom`.
    planId: 'run_mockplan01',
    status: 'open',
    rawTaskId: null,
    initiatedBy: 'user',
    owner: { login: 'ivndev001', avatar: 'https://avatars.githubusercontent.com/u/1?v=4' }, // resolved (#4477); plan_mock_custom is the null case
    createdIso: at('2026-07-15'),
    updatedIso: at('2026-07-17'),
    groups: liveGroups(),
  },
  {
    // `_done` is the terminal story — every slice merged, so the epic view it
    // opens has nothing left to stop and its control is ABSENT.
    planId: 'plan_mock_done',
    status: 'open',
    rawTaskId: 'rtk_mock01',
    initiatedBy: 'system',
    owner: null, // no person to name at mint
    createdIso: at('2026-07-02'),
    updatedIso: at('2026-07-11'),
    groups: doneGroups(),
  },
  {
    // Stopped by `plan cancel`, with a dead slice under it. `cancelled` over
    // dispatched/pending groups is not a contradiction: `cancelPlanTasks` flips
    // the TASKS and stamps only groups that had none, so the group rows say
    // nothing about the stop and only the plan row does.
    planId: 'plan_mock_stalled',
    status: 'cancelled',
    rawTaskId: null,
    initiatedBy: 'system',
    owner: null,
    createdIso: at('2026-06-20'),
    updatedIso: at('2026-06-28'),
    groups: stalledGroups(),
  },
  {
    // The cross-repo epic. `repoFullName`/`repoFullNames` fall out of the
    // groups below, so the lead-vs-alphabetical split is a property of the fixture.
    planId: 'plan_mock_multi',
    status: 'open',
    rawTaskId: 'rtk_mock02',
    initiatedBy: 'system',
    owner: null,
    createdIso: at('2026-07-20'),
    updatedIso: at('2026-07-22'),
    groups: multiRepoGroups(),
  },
];
